/**
 * API-Client für Betreuungsverwaltung
 * Basierend auf der Pengu-Betreuungsassistent Struktur
 */

import { authAPI } from './authAPI';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
const API_BASE = `${BACKEND_URL}/api/betreuung`;

/** Zentraler fetch mit Auth-Header und 401-Retry */
async function af(url, options = {}) {
  return authAPI.apiFetch(url, options);
}

class BetreuungAPI {
  // ==================== Connection Test ====================
  async testConnection() {
    const response = await af(`${API_BASE}/test-connection`);
    return response.json();
  }

  // ==================== Eingangspruefung (08.10.2026) ====================
  async _ep(pfad, opts) {
    const response = await af(`${BACKEND_URL}/api/eingangspruefung${pfad}`, opts);
    if (!response.ok) {
      let detail = 'Eingangspruefung: Fehler';
      try { detail = (await response.json()).detail || detail; } catch { /* kein JSON */ }
      throw new Error(detail);
    }
    return response.json();
  }
  eingangspruefungOffen() { return this._ep('/offen'); }
  eingangspruefungEntscheiden(dokumentId, body) {
    return this._ep(`/${dokumentId}/entscheidung`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  }
  eingangspruefungAuto(dryRun) { return this._ep(`/auto?dry_run=${dryRun ? 'true' : 'false'}`, { method: 'POST' }); }
  eingangspruefungAufgaben(dokumentId) { return this._ep(`/${dokumentId}/aufgaben`); }
  eingangspruefungOriginalLink(dokumentId) { return this._ep(`/${dokumentId}/original-link`, { method: 'POST' }); }

  // ==================== Neue Betreuung aus Beschluss (22.10.2026, Schalter NEUE_BETREUUNG_SCAN_AKTIV) ====================
  async _nb(pfad, body) {
    const opts = body === undefined ? {} : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
    const response = await af(`${BACKEND_URL}/api/neue-betreuung${pfad}`, opts);
    let daten = {};
    try { daten = await response.json(); } catch { /* kein JSON */ }
    if (!response.ok) {
      const fehler = new Error(daten.detail || 'Neue Betreuung: Fehler');
      fehler.status = response.status; fehler.daten = daten;
      throw fehler;
    }
    return daten;
  }
  neueBetreuungStatus() { return this._nb('/status'); }
  neueBetreuungOffen() { return this._nb('/offen'); }
  neueBetreuungVorschlag(dokumentId) { return this._nb('/vorschlag', { dokument_id: dokumentId }); }
  neueBetreuungBestaetigen(vorschlagId, body) { return this._nb(`/vorschlag/${vorschlagId}/bestaetigen`, body); }
  neueBetreuungAblehnen(vorschlagId) { return this._nb(`/vorschlag/${vorschlagId}/ablehnen`, {}); }
  neueBetreuungBeginnNachtragen(betreuungId, body) { return this._nb(`/betreuung/${betreuungId}/beginn`, body); }

  // ==================== Klienten ====================
  async listKlienten(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.prioritaet) searchParams.append('prioritaet', params.prioritaet);
    if (params.search) searchParams.append('search', params.search);
    
    const url = `${API_BASE}/klienten${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    if (!response.ok) throw new Error(`Serverfehler ${response.status}`);
    return response.json();
  }

  async getKlient(id) {
    const response = await af(`${API_BASE}/klienten/${id}`);
    if (!response.ok) throw new Error('Klient nicht gefunden');
    return response.json();
  }

  async createKlient(data) {
    const response = await af(`${API_BASE}/klienten`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async updateKlient(id, data) {
    const response = await af(`${API_BASE}/klienten/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Aktualisieren');
    }
    return response.json();
  }

  async deleteKlient(id) {
    const response = await af(`${API_BASE}/klienten/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Löschen');
    }
    return response.json();
  }

  async getKlientUebersicht(id) {
    const response = await af(`${API_BASE}/klienten/${id}/uebersicht`);
    if (!response.ok) throw new Error('Klient nicht gefunden');
    return response.json();
  }

  // ==================== Betreuungen ====================
  async listBetreuungen(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.klient_id) searchParams.append('klient_id', params.klient_id);
    if (params.status) searchParams.append('status', params.status);
    
    const url = `${API_BASE}/betreuungen${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async getBetreuung(id) {
    const response = await af(`${API_BASE}/betreuungen/${id}`);
    if (!response.ok) throw new Error('Betreuung nicht gefunden');
    return response.json();
  }

  async createBetreuung(data) {
    const response = await af(`${API_BASE}/betreuungen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async updateBetreuung(id, data) {
    const response = await af(`${API_BASE}/betreuungen/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Aktualisieren');
    }
    return response.json();
  }

  async deleteBetreuung(id) {
    const response = await af(`${API_BASE}/betreuungen/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Löschen');
    }
    return response.json();
  }

  // ==================== Aufgaben ====================
  async listAufgaben(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.klient_id) searchParams.append('klient_id', params.klient_id);
    if (params.status) searchParams.append('status', params.status);
    if (params.prioritaet) searchParams.append('prioritaet', params.prioritaet);
    if (params.kategorie) searchParams.append('kategorie', params.kategorie);
    if (params.eltern_aufgabe_id) searchParams.append('eltern_aufgabe_id', params.eltern_aufgabe_id);
    
    const url = `${API_BASE}/aufgaben${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async getAufgabe(id) {
    const response = await af(`${API_BASE}/aufgaben/${id}`);
    if (!response.ok) {
      // Status mitgeben: nur 404 heisst "nicht gefunden"; 502/503 (z. B. Backend-Neustart) ist ein Ladefehler (02.11.2026)
      const e = new Error(response.status === 404 ? 'Aufgabe nicht gefunden' : `Aufgabe konnte nicht geladen werden (HTTP ${response.status})`);
      e.status = response.status;
      throw e;
    }
    return response.json();
  }

  async createAufgabe(data) {
    const response = await af(`${API_BASE}/aufgaben`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async updateAufgabe(id, data) {
    const response = await af(`${API_BASE}/aufgaben/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Aktualisieren');
    }
    return response.json();
  }

  // ==================== Aufgabenverwaltung 2.0 (27.10.2026) ====================
  async _aufgabenJson(pfad, body) {
    const response = await af(`${API_BASE}/aufgaben${pfad}`, body === undefined ? {} : {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const daten = await response.json().catch(() => ({}));
    if (!response.ok) {
      const e = new Error(daten.detail || 'Aktion nicht möglich');
      e.status = response.status;
      throw e;
    }
    return daten;
  }

  /** Serverseitige Suche/Filter/Sortierung mit Seiten: {items, gesamt, ids, facetten} */
  sucheAufgaben(params = {}) {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '' && v !== 'alle') sp.append(k, v); });
    return this._aufgabenJson(`/suche?${sp.toString()}`);
  }

  aufgabenDubletten(klientId) { return this._aufgabenJson(`/dubletten?klient_id=${encodeURIComponent(klientId)}`); }

  sammelErledigenVorschau(aufgabeIds) { return this._aufgabenJson('/sammel-erledigen/vorschau', { aufgabe_ids: aufgabeIds }); }

  /** staende: {aufgabe_id: stand} aus der Vorschau */
  sammelErledigen(staende) { return this._aufgabenJson('/sammel-erledigen', { staende }); }

  zusammenfuehrenVorschlag(aufgabeIds) { return this._aufgabenJson('/zusammenfuehren/vorschlag', { aufgabe_ids: aufgabeIds }); }

  zusammenfuehren(payload) { return this._aufgabenJson('/zusammenfuehren', payload); }

  /** ZEO zu Aufgaben befragen – nur lesend: {klient_id, frage, modus: 'auswahl'|'klient', aufgabe_ids} */
  zeoFrageAufgaben(payload) { return this._aufgabenJson('/zeo-frage', payload); }

  unteraufgabenAnlegen(aufgabeId, titel) { return this._aufgabenJson(`/${aufgabeId}/unteraufgaben`, { titel }); }

  dokumentMitAufgabeVerknuepfen(aufgabeId, dokumentId) { return this._aufgabenJson(`/${aufgabeId}/dokument-verknuepfen`, { dokument_id: dokumentId }); }

  /** Gewählte Aufgaben als Unteraufgaben unter eine Hauptaufgabe hängen (nur derselbe Klient, nichts wird gelöscht) */
  async zusammenfassenAufgaben(hauptAufgabeId, aufgabeIds) {
    const response = await af(`${API_BASE}/aufgaben/zusammenfassen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ haupt_aufgabe_id: hauptAufgabeId, aufgabe_ids: aufgabeIds })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Zusammenfassen nicht möglich');
    }
    return response.json();
  }

  /** Kommunikationsentwurf (fax | email | kurznachricht | brief) aus einer Aufgabe – NUR Entwurf, kein Versand */
  async kommunikationsEntwurf(aufgabeId, kanal) {
    const response = await af(`${API_BASE}/aufgaben/${aufgabeId}/kommunikationsentwurf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kanal })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Entwurf nicht möglich');
    }
    return response.json();
  }

  /** Unteraufgabe -> Entwurf in der Schreibstube (Brief / E-Mail / Fax); derselbe Entwurf je Aufgabe + Versandweg. Kein Versand. */
  async schreibstubeEntwurf(aufgabeId, kanal) {
    const response = await af(`${API_BASE}/aufgaben/${aufgabeId}/schreibstube-entwurf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kanal })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Korrespondenz: Entwurf nicht möglich');
    }
    return response.json();
  }

  async deleteAufgabe(id) {
    const response = await af(`${API_BASE}/aufgaben/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Löschen');
    }
    return response.json();
  }

  async getAufgabeDetail(id) {
    const response = await af(`${API_BASE}/aufgaben/${id}/detail`);
    if (!response.ok) throw new Error('Aufgabe nicht gefunden');
    return response.json();
  }

  async listAufgabenVerlauf(aufgabeId) {
    const response = await af(`${API_BASE}/aufgaben/${aufgabeId}/verlauf`);
    return response.json();
  }

  async createAufgabenVerlauf(aufgabeId, data) {
    const response = await af(`${API_BASE}/aufgaben/${aufgabeId}/verlauf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  }

  async deleteAufgabenVerlauf(aufgabeId, verlaufId) {
    const response = await af(`${API_BASE}/aufgaben/${aufgabeId}/verlauf/${verlaufId}`, {
      method: 'DELETE'
    });
    return response.json();
  }

  // ==================== Dokumente ====================
  async listDokumente(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.klient_id) searchParams.append('klient_id', params.klient_id);
    if (params.dokumententyp) searchParams.append('dokumententyp', params.dokumententyp);
    if (params.search) searchParams.append('search', params.search);
    if (params.ordner_id !== undefined) searchParams.append('ordner_id', params.ordner_id);
    if (params.aufgabe_id) searchParams.append('aufgabe_id', params.aufgabe_id);
    if (params.vorgang_id) searchParams.append('vorgang_id', params.vorgang_id);
    
    const url = `${API_BASE}/dokumente${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async getDokument(id) {
    const response = await af(`${API_BASE}/dokumente/${id}`);
    if (!response.ok) throw new Error('Dokument nicht gefunden');
    return response.json();
  }

  async createDokument(data) {
    const response = await af(`${API_BASE}/dokumente`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async updateDokument(id, data) {
    const response = await af(`${API_BASE}/dokumente/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Aktualisieren');
    }
    return response.json();
  }

  async bulkKlientZuordnung(dokumentIds, klientId, klientName) {
    const response = await af(`${API_BASE}/dokumente/bulk-zuordnung`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dokument_ids: dokumentIds, klient_id: klientId, klient_name: klientName })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler bei Bulk-Zuordnung');
    }
    return response.json();
  }

  async uploadDokument(file, { titel = '', dokumententyp = 'sonstiges', klient_id = '', aufgabe_id = '', person_id = '', person_typ = '' } = {}) {
    const formData = new FormData();
    formData.append('datei', file);
    formData.append('titel', titel || file.name);
    formData.append('dokumententyp', dokumententyp);
    formData.append('klient_id', klient_id);
    formData.append('aufgabe_id', aufgabe_id);
    formData.append('person_id', person_id);
    formData.append('person_typ', person_typ);

    const response = await af(`${API_BASE}/dokumente/upload`, {
      method: 'POST',
      body: formData
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Hochladen');
    }
    return response.json();
  }

  async deleteDokument(id) {
    const response = await af(`${API_BASE}/dokumente/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Löschen');
    }
    return response.json();
  }

  async renameDokument(id, titel, dateiname) {
    const body = {};
    if (titel) body.titel = titel;
    if (dateiname) body.dateiname = dateiname;
    const response = await af(`${API_BASE}/dokumente/${id}/rename`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Umbenennen');
    }
    return response.json();
  }

  async linkDokumentAufgabe(dokumentId, aufgabeId) {
    const response = await af(`${API_BASE}/dokumente/${dokumentId}/aufgabe-verknuepfen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ aufgabe_id: aufgabeId })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Verknüpfen');
    }
    return response.json();
  }

  async unlinkDokumentAufgabe(dokumentId, aufgabeId) {
    const response = await af(`${API_BASE}/dokumente/${dokumentId}/aufgabe-verknuepfen/${aufgabeId}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Entfernen');
    }
    return response.json();
  }

  async listAufgabeDokumente(aufgabeId) {
    const response = await af(`${API_BASE}/aufgaben/${aufgabeId}/dokumente`);
    if (!response.ok) return [];
    return response.json();
  }

  // ==================== Dokumenten-Ordner ====================
  async listOrdner(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.klient_id) searchParams.append('klient_id', params.klient_id);
    if (params.parent_id) searchParams.append('parent_id', params.parent_id);
    if (params.include_counts !== undefined) searchParams.append('include_counts', params.include_counts);
    
    const url = `${API_BASE}/ordner${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async getOrdner(id) {
    const response = await af(`${API_BASE}/ordner/${id}`);
    if (!response.ok) throw new Error('Ordner nicht gefunden');
    return response.json();
  }

  async createOrdner(data) {
    const response = await af(`${API_BASE}/ordner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async updateOrdner(id, data) {
    const response = await af(`${API_BASE}/ordner/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Aktualisieren');
    }
    return response.json();
  }

  async deleteOrdner(id, moveDocsTo = null) {
    const url = moveDocsTo 
      ? `${API_BASE}/ordner/${id}?move_docs_to=${moveDocsTo}`
      : `${API_BASE}/ordner/${id}`;
    const response = await af(url, { method: 'DELETE' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Löschen');
    }
    return response.json();
  }

  async renameOrdner(id, name) {
    const response = await af(`${API_BASE}/ordner/${id}/rename`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Umbenennen');
    }
    return response.json();
  }


  async moveDokumenteToOrdner(dokumentIds, ordnerId = null) {
    const response = await af(`${API_BASE}/ordner/move-dokumente`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dokument_ids: dokumentIds, ordner_id: ordnerId })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Verschieben');
    }
    return response.json();
  }

  async createStandardOrdner(klientId) {
    const response = await af(`${API_BASE}/ordner/standard/${klientId}`, {
      method: 'POST'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  // ==================== Juristische Personen ====================
  async listJuristischePersonen(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.typ) searchParams.append('typ', params.typ);
    if (params.search) searchParams.append('search', params.search);
    
    const url = `${API_BASE}/juristische-personen${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async getJuristischePerson(id) {
    const response = await af(`${API_BASE}/juristische-personen/${id}`);
    if (!response.ok) throw new Error('Institution nicht gefunden');
    return response.json();
  }

  async createJuristischePerson(data) {
    const response = await af(`${API_BASE}/juristische-personen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async updateJuristischePerson(id, data) {
    const response = await af(`${API_BASE}/juristische-personen/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Aktualisieren');
    }
    return response.json();
  }

  async deleteJuristischePerson(id) {
    const response = await af(`${API_BASE}/juristische-personen/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Löschen');
    }
    return response.json();
  }

  // ==================== Natürliche Personen ====================
  async listNatuerlichePersonen(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.rolle) searchParams.append('rolle', params.rolle);
    if (params.firma_id) searchParams.append('firma_id', params.firma_id);
    if (params.klient_id) searchParams.append('klient_id', params.klient_id);
    if (params.search) searchParams.append('search', params.search);
    
    const url = `${API_BASE}/natuerliche-personen${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async getNatuerlichePerson(id) {
    const response = await af(`${API_BASE}/natuerliche-personen/${id}`);
    if (!response.ok) throw new Error('Person nicht gefunden');
    return response.json();
  }

  async createNatuerlichePerson(data) {
    const response = await af(`${API_BASE}/natuerliche-personen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async updateNatuerlichePerson(id, data) {
    const response = await af(`${API_BASE}/natuerliche-personen/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Aktualisieren');
    }
    return response.json();
  }

  async deleteNatuerlichePerson(id) {
    const response = await af(`${API_BASE}/natuerliche-personen/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Löschen');
    }
    return response.json();
  }

  // ==================== Personen Statistiken ====================
  async getPersonenStats() {
    const response = await af(`${API_BASE}/personen/stats`);
    return response.json();
  }

  // ==================== Maßnahmen ====================
  async listMassnahmen(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.person_id) searchParams.append('person_id', params.person_id);
    if (params.typ) searchParams.append('typ', params.typ);
    if (params.status) searchParams.append('status', params.status);
    
    const url = `${API_BASE}/massnahmen${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async createMassnahme(data) {
    const response = await af(`${API_BASE}/massnahmen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async deleteMassnahme(id) {
    const response = await af(`${API_BASE}/massnahmen/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Löschen');
    }
    return response.json();
  }

  // ==================== Maßnahmenkatalog ====================
  async listMassnahmenkatalog(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.kategorie) searchParams.append('kategorie', params.kategorie);
    if (params.aktiv !== undefined) searchParams.append('aktiv', params.aktiv);
    
    const url = `${API_BASE}/massnahmenkatalog${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async initMassnahmenkatalog() {
    const response = await af(`${API_BASE}/massnahmenkatalog/init-defaults`, {
      method: 'POST'
    });
    return response.json();
  }

  // ==================== Aufgabenkreise ====================
  async getAufgabenkreiseKatalog() {
    const response = await af(`${API_BASE}/aufgabenkreise/katalog`);
    return response.json();
  }

  async listAufgabenkreise(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.klient_id) searchParams.append('klient_id', params.klient_id);
    if (params.aktiv !== undefined) searchParams.append('aktiv', params.aktiv);
    
    const url = `${API_BASE}/aufgabenkreise${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async createAufgabenkreis(data) {
    const response = await af(`${API_BASE}/aufgabenkreise`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async deleteAufgabenkreis(id) {
    const response = await af(`${API_BASE}/aufgabenkreise/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Löschen');
    }
    return response.json();
  }

  // ==================== Vergütungsanträge ====================
  async listVerguetungsantraege(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.klient_id) searchParams.append('klient_id', params.klient_id);
    if (params.status) searchParams.append('status', params.status);
    
    const url = `${API_BASE}/verguetungsantraege${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async createVerguetungsantrag(data) {
    const response = await af(`${API_BASE}/verguetungsantraege`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async updateVerguetungsantrag(id, data) {
    const response = await af(`${API_BASE}/verguetungsantraege/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Aktualisieren');
    }
    return response.json();
  }

  async deleteVerguetungsantrag(id) {
    const response = await af(`${API_BASE}/verguetungsantraege/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Fehler beim Löschen');
    return response.json();
  }

  // ==================== Rechnungslegungen ====================
  async listRechnungslegungen(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.klient_id) searchParams.append('klient_id', params.klient_id);
    if (params.status) searchParams.append('status', params.status);
    
    const url = `${API_BASE}/rechnungslegungen${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async createRechnungslegung(data) {
    const response = await af(`${API_BASE}/rechnungslegungen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async updateRechnungslegung(id, data) {
    const response = await af(`${API_BASE}/rechnungslegungen/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Aktualisieren');
    }
    return response.json();
  }

  async deleteRechnungslegung(id) {
    const response = await af(`${API_BASE}/rechnungslegungen/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Fehler beim Löschen');
    return response.json();
  }

  // ==================== Betreuungsakte 360° Übersicht ====================
  async getKlientUebersicht(klientId) {
    const response = await af(`${API_BASE}/klienten/${klientId}/uebersicht`);
    if (!response.ok) {
      throw new Error('Übersicht nicht gefunden');
    }
    return response.json();
  }

  // ==================== Korrespondenz ====================
  async listKorrespondenz(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.klient_id) searchParams.append('klient_id', params.klient_id);
    if (params.kanal) searchParams.append('kanal', params.kanal);
    if (params.richtung) searchParams.append('richtung', params.richtung);
    if (params.search) searchParams.append('search', params.search);
    
    const url = `${API_BASE}/korrespondenz${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async getKorrespondenz(id) {
    const response = await af(`${API_BASE}/korrespondenz/${id}`);
    if (!response.ok) throw new Error('Korrespondenz nicht gefunden');
    return response.json();
  }

  async createKorrespondenz(data) {
    const response = await af(`${API_BASE}/korrespondenz`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  async updateKorrespondenz(id, data) {
    const response = await af(`${API_BASE}/korrespondenz/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Aktualisieren');
    }
    return response.json();
  }

  async deleteKorrespondenz(id) {
    const response = await af(`${API_BASE}/korrespondenz/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Löschen');
    }
    return response.json();
  }

  async getVerknuepfteKorrespondenz(entityTyp, entityId) {
    const response = await af(`${API_BASE}/korrespondenz/verknuepft/${entityTyp}/${entityId}`);
    return response.json();
  }

  // ==================== Kontobewegungen ====================
  async listKontobewegungen(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.klient_id) searchParams.append('klient_id', params.klient_id);
    if (params.typ) searchParams.append('typ', params.typ);
    
    const url = `${API_BASE}/kontobewegungen${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async createKontobewegung(data) {
    const response = await af(`${API_BASE}/kontobewegungen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen');
    }
    return response.json();
  }

  // ==================== Fallhistorie ====================
  async listFallhistorie(klient_id) {
    const response = await af(`${API_BASE}/fallhistorie?klient_id=${klient_id}`);
    return response.json();
  }

  // ==================== Dashboard ====================
  async getDashboardStats() {
    const response = await af(`${API_BASE}/dashboard/stats`);
    return response.json();
  }
  // ==================== KI-Assistent ====================
  async chatAssistent(data) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Chat');
    }
    return response.json();
  }

  // ZEO Mittwald Deep-Run Status/Ergebnis (Produktionskorrektur 05.10.2026) —
  // nur relevant, wenn ein Chat-Response modus="mittwald_deep_running" liefert.
  async zeoMittwaldRunResult(runId) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/zeo-mittwald/runs/${runId}/result`);
    if (!response.ok) throw new Error('ZEO-Lauf nicht gefunden');
    return response.json();
  }

  // ==================== ZEO /zeo Workspace (Pilot-UX 07.10.2026) ====================
  // Klient-/Aufgabenauswahl, persistenter Chat-Fokus (Entity-Refs), + Neue Aufgabe (Dedup serverseitig).
  async _zeoWs(pfad, opts) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/zeo-workspace${pfad}`, opts);
    if (!response.ok) {
      let detail = 'ZEO-Workspace-Fehler';
      try { detail = (await response.json()).detail || detail; } catch { /* kein JSON */ }
      throw new Error(detail);
    }
    return response.json();
  }

  // Chat-Anhaenge (08.10.2026): Upload vor dem Senden; Abruf nur ueber die zugehoerige Session (Blob, Auth-Header). KEINE Aktenablage.
  async zeoAnhaengeHochladen(dateien) {
    const fd = new FormData();
    dateien.forEach((f) => fd.append('dateien', f, f.name));
    return this._zeoWs('/anhaenge', { method: 'POST', body: fd });
  }
  zeoAnhangVerwerfen(anhangId) { return this._zeoWs(`/anhaenge/${anhangId}`, { method: 'DELETE' }); }
  async zeoAnhangBlob(sessionId, anhangId) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/zeo-workspace/session/${sessionId}/anhaenge/${anhangId}`);
    if (!response.ok) throw new Error('Anhang nicht verfügbar');
    return response.blob();
  }

  zeoWorkspaceKlienten(q = '') { return this._zeoWs(`/klienten?q=${encodeURIComponent(q)}`); }
  zeoWorkspaceAufgaben(klientId) { return this._zeoWs(`/klient/${klientId}/aufgaben`); }
  zeoWorkspaceVorgaenge(klientId) { return this._zeoWs(`/klient/${klientId}/vorgaenge`); }
  zeoWorkspaceSessionErstellen(data) {
    return this._zeoWs('/session', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
  }
  zeoWorkspaceKontext(sessionId) { return this._zeoWs(`/session/${sessionId}/kontext`); }
  zeoWorkspaceEntities(sessionId, data) {
    return this._zeoWs(`/session/${sessionId}/entities`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
  }
  zeoWorkspaceTitel(sessionId, titel) {
    return this._zeoWs(`/session/${sessionId}/titel`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ titel }) });
  }
  zeoWorkspaceSessionsSuche(q, klientId) {
    return this._zeoWs(`/sessions/suche?q=${encodeURIComponent(q)}${klientId ? `&klient_id=${encodeURIComponent(klientId)}` : ''}`);
  }
  zeoWorkspaceAufgabeAnlegen(data) {
    return this._zeoWs('/aufgabe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
  }

  async listAssistentSessions() {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/sessions`);
    return response.json();
  }

  async getAssistentSession(sessionId) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/sessions/${sessionId}`);
    if (!response.ok) throw new Error('Session nicht gefunden');
    return response.json();
  }

  // ZEO interaktive Antworten (06.10.2026): Dokument-Zuordnung NUR nach
  // expliziter Nutzerbestaetigung -- kein automatischer Write aus dem Chat.
  async zeoDokumentZuordnungBestaetigen(dokumentId, klientId) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/zeo/dokument-zuordnung/bestaetigen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dokument_id: dokumentId, klient_id: klientId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Zuordnung fehlgeschlagen');
    }
    return response.json();
  }

  async deleteAssistentSession(sessionId) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/sessions/${sessionId}`, {
      method: 'DELETE'
    });
    return response.json();
  }

  async pseudonymize(data) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/pseudonymize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  }

  async executeAssistentAktion(data) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/aktion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  }

  async rechercheMitDokumenten(anfrage, { aufgabeId = '', kontextTyp = '', kontextId = '', dateien = [] } = {}) {
    const formData = new FormData();
    formData.append('anfrage', anfrage);
    formData.append('aufgabe_id', aufgabeId);
    formData.append('kontext_typ', kontextTyp);
    formData.append('kontext_id', kontextId);
    for (const f of dateien) {
      formData.append('dateien', f);
    }
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/recherche-mit-dokumenten`, {
      method: 'POST',
      body: formData
    });
    return response.json();
  }

  async rechercheAbsenden(anfrage, pseudonymisierterText, kontextId = '') {
    const formData = new FormData();
    formData.append('anfrage', anfrage);
    formData.append('pseudonymisierter_text', pseudonymisierterText);
    formData.append('kontext_id', kontextId);
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/recherche-absenden`, {
      method: 'POST',
      body: formData
    });
    return response.json();
  }

  // Chat mit Datei-Upload (wie Posteingang)
  async chatMitDatei(datei, nachricht = '', kontextTyp = '', kontextId = '', sessionId = '') {
    const formData = new FormData();
    formData.append('datei', datei);
    formData.append('nachricht', nachricht);
    formData.append('kontext_typ', kontextTyp);
    formData.append('kontext_id', kontextId);
    formData.append('session_id', sessionId);  // WICHTIG: Session für isolierten Kontext
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/chat-mit-datei`, {
      method: 'POST',
      body: formData
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Datei-Upload');
    }
    return response.json();
  }

  // Text-to-Speech mit OpenAI Nova Stimme
  async textToSpeech(text, voice = 'thorsten-high', speed = 1.0) {
    const token = localStorage.getItem('olympus_token');
    const response = await af(`${API_BASE.replace('/betreuung', '')}/sprache/tts`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ text, voice, speed })
    });
    if (!response.ok) {
      throw new Error('TTS Fehler');
    }
    return response.json();
  }

  // Speech-to-Text mit lokalem Whisper (DSGVO-konform)
  async speechToText(audioBlob, language = 'de') {
    const token = localStorage.getItem('olympus_token');
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    
    // Zuerst lokales Whisper versuchen (DSGVO-konform)
    try {
      const response = await af(`${API_BASE.replace('/betreuung', '')}/voice/stt`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        return { success: true, text: data.text, dauer: data.duration };
      }
    } catch (e) { /* Fallback */ }

    // Fallback auf alten Endpoint
    const formData2 = new FormData();
    formData2.append('audio', audioBlob, 'recording.webm');
    formData2.append('language', language);
    const response = await af(`${API_BASE.replace('/betreuung', '')}/sprache/stt`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData2
    });
    if (!response.ok) {
      throw new Error('STT Fehler');
    }
    return response.json();
  }

  // Text-to-Speech: ElevenLabs (primaer) → Piper (fallback)
  async textToSpeechElevenLabs(text, voiceId = null) {
    const token = localStorage.getItem('olympus_token');
    const response = await af(`${API_BASE.replace('/betreuung', '')}/voice/tts`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ text, voice_id: voiceId })
    });
    if (!response.ok) {
      throw new Error('ElevenLabs TTS nicht verfuegbar');
    }
    return response.json();
  }

  // Intelligente Fallabfrage mit optionaler Sprachausgabe
  async fallabfrage(frage, klientId = null, mitSprache = true, voice = 'thorsten-high') {
    const token = localStorage.getItem('olympus_token');
    const response = await af(`${API_BASE.replace('/betreuung', '')}/sprache/fallabfrage`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        frage,
        klient_id: klientId,
        mit_sprache: mitSprache,
        voice
      })
    });
    if (!response.ok) {
      throw new Error('Fallabfrage Fehler');
    }
    return response.json();
  }

  // Verfügbare TTS-Stimmen
  async getVoices() {
    const token = localStorage.getItem('olympus_token');
    const response = await af(`${API_BASE.replace('/betreuung', '')}/sprache/stimmen`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    return response.json();
  }

  // ==================== Änderungswünsche ====================
  async listFeatureRequests(params = {}) {
    const sp = new URLSearchParams();
    if (params.status) sp.append('status', params.status);
    const url = `${API_BASE}/feature-requests${sp.toString() ? '?' + sp.toString() : ''}`;
    const response = await af(url);
    return response.json();
  }

  async createFeatureRequest(data) {
    const response = await af(`${API_BASE}/feature-requests`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
    });
    return response.json();
  }

  async updateFeatureRequest(id, data) {
    const response = await af(`${API_BASE}/feature-requests/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
    });
    return response.json();
  }

  async deleteFeatureRequest(id) {
    const response = await af(`${API_BASE}/feature-requests/${id}`, { method: 'DELETE' });
    return response.json();
  }

  // ==================== Kommunikation (Versand) ====================
  async getKommunikationSettings() {
    const response = await af(`${BACKEND_URL}/api/kommunikation/einstellungen`);
    return response.json();
  }

  async getKanalSettings(kanal) {
    const response = await af(`${BACKEND_URL}/api/kommunikation/einstellungen/${kanal}`);
    return response.json();
  }

  async saveKanalSettings(data) {
    const response = await af(`${BACKEND_URL}/api/kommunikation/einstellungen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  }

  async testKanal(kanal) {
    const response = await af(`${BACKEND_URL}/api/kommunikation/einstellungen/${kanal}/test`, {
      method: 'POST'
    });
    return response.json();
  }

  async getChannelsStatus() {
    const response = await af(`${BACKEND_URL}/api/kommunikation/status`);
    return response.json();
  }

  async sendEmail(data) {
    const response = await af(`${BACKEND_URL}/api/kommunikation/senden/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  }

  async sendEBO(data) {
    const response = await af(`${BACKEND_URL}/api/kommunikation/senden/ebo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  }

  async sendFax(data) {
    const response = await af(`${BACKEND_URL}/api/kommunikation/senden/fax`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  }

  async logTelefon(data) {
    const response = await af(`${BACKEND_URL}/api/kommunikation/senden/telefon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  }

  // ==================== Nachanalyse mit Eckdaten ====================
  
  /**
   * Analysiert ein Dokument erneut mit Hinweisen vom Benutzer.
   * @param {string} dokumentId - ID des Dokuments
   * @param {object} eckdaten - z.B. {nachname: "Müller", vorname: "Hans", hinweis: "Person im Brief erwähnt"}
   */
  async nachanalyseMitEckdaten(dokumentId, eckdaten) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/nachanalyse-mit-eckdaten`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dokument_id: dokumentId, eckdaten })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler bei der Nachanalyse');
    }
    return response.json();
  }

  /**
   * Analysiert ein Formular/Antrag und identifiziert alle Felder.
   * Füllt bekannte Felder automatisch mit Daten des Betreuten aus.
   * @param {string} dokumentId - ID des Dokuments
   * @param {string} klientId - Optional: ID des Betreuten für automatisches Ausfüllen
   */
  async formularAnalyse(dokumentId, klientId = null) {
    const response = await af(`${API_BASE.replace('/betreuung', '')}/assistent/formular-analyse`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dokument_id: dokumentId, klient_id: klientId })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler bei der Formular-Analyse');
    }
    return response.json();
  }

  /**
   * Erstellt automatisch eine Wiedervorlage für einen Bescheid.
   * @param {string} klientId - ID des Betreuten
   * @param {string} bescheidTyp - Art des Bescheids (rente, pflege, etc.)
   * @param {string} gueltigBis - Gültigkeitsdatum (YYYY-MM-DD)
   * @param {string} dokumentId - Optional: ID des Dokuments
   * @param {number} vorlaufWochen - Wochen vor Ablauf (Standard: 4)
   */
  async createBescheidWiedervorlage(klientId, bescheidTyp, gueltigBis, dokumentId = null, vorlaufWochen = 4) {
    const response = await af(`${API_BASE}/bescheid-wiedervorlage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        klient_id: klientId, 
        bescheid_typ: bescheidTyp, 
        gueltig_bis: gueltigBis,
        dokument_id: dokumentId,
        vorlauf_wochen: vorlaufWochen
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Erstellen der Wiedervorlage');
    }
    return response.json();
  }
  // ==================== Klienten-Verknüpfungen ====================
  async getKlientVerknuepfungen(klientId) {
    const response = await af(`${API_BASE}/klienten/${klientId}/verknuepfungen`);
    if (!response.ok) throw new Error('Verknüpfungen konnten nicht geladen werden');
    return response.json();
  }

  async addKlientVerknuepfung(klientId, data) {
    const response = await af(`${API_BASE}/klienten/${klientId}/verknuepfungen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Verknüpfen');
    }
    return response.json();
  }

  async removeKlientVerknuepfung(klientId, zielKlientId) {
    const response = await af(`${API_BASE}/klienten/${klientId}/verknuepfungen/${zielKlientId}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Fehler beim Entfernen');
    }
    return response.json();
  }
}

export const betreuungAPI = new BetreuungAPI();
export default betreuungAPI;
